// src/pages/NoticePage.js
/**
 * NoticePage
 * -----------------------------------------------------------------------------
 * Renders a translated notice whose message key is selected from the URL
 * query string: `?source=<Key>`.
 *
 * The component looks up translations using the pattern:
 *   t(`notice.${source}`)
 *
 * Where `source` comes from the query param. The value is sanitized to the
 * safe character set `[A-Za-z0-9._-]` so it can be safely used in i18n keys.
 *
 * Robust fallback rules:
 *   1) notice.<exact source>                (e.g., "notice.Questionnaire")
 *   2) notice.<Capitalized source>          (e.g., "notice.Questionnaire")
 *   3) notice.<lowercased source>           (e.g., "notice.questionnaire")
 *   4) notice.default                       (final fallback)
 *
 * Example routes → keys:
 *   /notice?source=Questionnaire  → t("notice.Questionnaire")
 *   /notice?source=recording      → t("notice.recording") or fallback to "notice.default"
 *
 * i18n JSON example:
 *   {
 *     "notice": {
 *       "default": "Heads up: something to tell you.",
 *       "Questionnaire": "Before the questionnaire, please review …",
 *       "Recording": "Before recording, please ensure …"
 *     }
 *   }
 *
 * About `source`:
 * - Intended values include things like "Questionnaire", "Recording",
 *   "Auth", "Home", etc. You control these by linking to /notice with the
 *   appropriate `?source=` value from elsewhere in the app.
 * - If the provided source doesn't have a matching translation key, we
 *   gracefully fall back to `notice.default`.
 *
 * @returns {JSX.Element}
 */

import React, { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { trackEvent } from "../utils/analytics/track";

function NoticePage() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();

  // Read `?source=...` from the URL (default to "default" if absent)
  const rawSource = params.get("source") ?? "default";

  // Sanitize to a safe subset for i18n keys
  const safeSource = rawSource.replace(/[^a-z0-9._-]/gi, "");

  // Build a small set of candidate keys with sensible fallbacks
  const capitalized =
    safeSource.length > 0
      ? safeSource[0].toUpperCase() + safeSource.slice(1)
      : "Default";

  const candidates = [
    `notice.${safeSource}`, // exact
    `notice.${capitalized}`, // Capitalized
    `notice.${safeSource.toLowerCase()}`, // lowercased
    "notice.default", // final fallback
  ];

  // Pick the first key that exists; fall back to "notice.default" if needed
  const key = candidates.find((k) => i18n.exists(k)) || "notice.default";

  useEffect(() => {
    // Track the source that led here (sanitised).
    trackEvent("notice_view", { source: safeSource || "default" });
  }, [safeSource]);

  return (
    <div className="w-full max-w-lg h-full p-4 flex flex-col justify-end md:justify-start items-center gap-4">
      <p>{t(key)}</p>
      <Link
        to="/home"
        className="home-button"
        onClick={() =>
          trackEvent("notice_click_home", { source: safeSource || "default" })
        }
      >
        {t("notice.home")}
      </Link>
    </div>
  );
}

export default memo(NoticePage);
